import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  BarChart3,
  ArrowUpRight,
  Check,
  Users,
  Award,
  Target,
  Clock,
  Zap,
  Heart,
  ChevronDown,
  ChevronUp,
  FileText,
  Download,
} from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: BarChart3, title: 'Real-time dashboards', text: 'View live ticket volume, agent status, and queue performance at a glance.' },
  { icon: Award, title: 'Agent performance', text: 'Track response time, resolution time, first contact resolution, and CSAT by agent.' },
  { icon: Target, title: 'SLA monitoring', text: 'Set SLA targets with automated alerts for breaches and near-misses.' },
  { icon: Heart, title: 'CSAT tracking', text: 'Measure customer satisfaction and feedback trends across all channels.' },
];

const benefits = [
  { icon: Clock, title: 'Real-time data', description: 'Metrics update every few seconds so you always have current insights.' },
  { icon: Users, title: 'Team insights', description: 'Identify top performers and coach agents based on performance data.' },
  { icon: Target, title: 'SLA compliance', description: 'Monitor SLA targets with automated alerts to prevent breaches.' },
  { icon: Download, title: 'Easy exports', description: 'Export any report as CSV or PDF and share with stakeholders.' },
];

const useCases = [
  { title: 'Improve response times', text: 'Identify bottlenecks and optimize agent workflows to reduce first response time.' },
  { title: 'Boost CSAT scores', text: 'Track satisfaction trends and coach agents based on real customer feedback.' },
  { title: 'Meet SLA targets', text: 'Monitor SLA compliance with automated alerts and prevent breaches.' },
  { title: 'Data-driven decisions', text: 'Use comprehensive analytics to make informed support strategy decisions.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'How fast does the analytics data update?',
    answer: 'Analytics dashboards update in real-time. Ticket volume, agent status, and queue metrics refresh every few seconds.',
  },
  {
    question: 'Can I export analytics data?',
    answer: 'Yes. You can export any report as CSV or PDF from the workspace. You can also schedule automated reports to be sent via email.',
  },
  {
    question: 'What metrics are tracked for agent performance?',
    answer: 'We track first response time, resolution time, first contact resolution rate, CSAT scores, ticket volume, and productivity metrics for each agent.',
  },
  {
    question: 'Can I set custom SLA targets?',
    answer: 'Yes. You can set custom SLA targets for response time and resolution time by ticket priority and channel. Automated alerts notify you of breaches.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ReportingAndAnalytics() {
  return (
    <>
      <SEO
        title="Reporting and Analytics - Support Metrics Dashboard | ZuriHub"
        description="Comprehensive support analytics with real-time dashboards for ticket volume, agent performance, CSAT tracking, SLA monitoring, and response times. Track KPIs and make data-driven decisions."
        path="/reporting-and-analytics"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <BarChart3 className="h-4 w-4" /> Pro & Enterprise
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Support analytics that <span className="gradient-text">drive decisions</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Real-time dashboards for ticket volume, agent performance, CSAT tracking, SLA monitoring, and response times. Track KPIs and optimize your support operations.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <BarChart3 className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Analytic%20header.png"
                  alt="ZuriHub Reporting and Analytics dashboard showing support metrics and KPIs"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful analytics features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to measure support performance.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why teams choose analytics</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform support operations.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                Connect your channels, collect data automatically, and start making data-driven decisions to improve your support operations.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: Zap,
                    title: 'Connect channels',
                    description: 'Integrate WhatsApp, email, live chat, and other support channels into ZuriHub.',
                  },
                  {
                    icon: BarChart3,
                    title: 'Collect data',
                    description: 'Ticket volume, response times, and customer feedback automatically populate dashboards.',
                  },
                  {
                    icon: Target,
                    title: 'Set KPIs',
                    description: 'Define targets for response time, resolution time, and customer satisfaction.',
                  },
                  {
                    icon: FileText,
                    title: 'Export reports',
                    description: 'Track performance in real-time, export reports, and share insights with stakeholders.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Analytics features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Real-time support dashboards</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Agent performance metrics</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> SLA monitoring and alerts</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> CSAT and NPS tracking</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Response time analytics</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> CSV and PDF exports</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Start tracking your support metrics</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Get real-time dashboards, agent performance tracking, and SLA monitoring in one workspace.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Create your workspace <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about analytics</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
